/**
 * Calendar Route Component
 *
 * Main calendar management page that displays appointments in a calendar view.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import { z } from 'zod';

import PageTransition from '@/components/ui/PageTransition';
import {
  useListResourcesQuery,
  useListClientRescheduleRequestsQuery,
  type Appointment,
  type AppointmentStatus,
  type CalendarVisibilityMode
} from '@/api/mcp-appointments';
import { getFeedVisibilityMode } from '@/utils/calendarIcs';
import { useAppointments } from '@/contexts/AppointmentContext';
import {
  useAppointmentStats,
  useCalendarImportFeedsByResources,
  useManageCalendarAppointments
} from '@/hooks/appointments';
import { AUTOREFRESH_INTERVAL } from '@/constants/autorefresh';
import {
  AppointmentsCalendar,
  CalendarHeader,
  CalendarStats,
  CalendarFilters
} from '@/components/appointments';
import { CalendarExternalConflictAlert } from '@/components/appointments/calendar/CalendarExternalConflictAlert';
import { PendingChangeRequestsDrawer } from '@/components/appointments/manage/PendingChangeRequestsDrawer';
import {
  collectExternalConflictItems,
  type CalendarResourceFilter
} from '@/components/appointments/calendar/calendarUtils';
import type { CalendarVisibleRange } from '@/components/appointments/calendar/AppointmentsCalendar';
import { useLastUpdatedText } from '@/hooks/useLastUpdatedText';
import { getResourceDisplayName } from '@/utils/serviceResourceLinks';
import { booleanSearchParam } from '@/utils/searchParams';

const calendarSearchSchema = z.object({
  status: z
    .enum(['ALL', 'SCHEDULED', 'CONFIRMED', 'PENDING', 'CANCELLED', 'COMPLETED', 'NO_SHOW', 'IN_PROGRESS'])
    .catch('ALL'),
  resource: z.string().catch('ALL'),
  external: booleanSearchParam(true)
});

export type ManageCalendarSearch = z.infer<typeof calendarSearchSchema>;

const buildCalendarSearch = ({
  status,
  resource,
  external
}: ManageCalendarSearch): ManageCalendarSearch => ({
  status,
  resource,
  external
});

export const Route = createFileRoute('/manage/calendar')({
  validateSearch: (search) => calendarSearchSchema.parse(search),
  component: RouteComponent
});

function RouteComponent() {
  const { t } = useTranslation('manage');
  const navigate = Route.useNavigate();
  const { status, resource, external } = Route.useSearch();
  const { openCreate, registerRescheduleWithAI } = useAppointments();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  /** Set from FullCalendar `datesSet` — null until the grid reports its visible range. */
  const [visibleRange, setVisibleRange] = useState<CalendarVisibleRange | null>(null);
  const [conflictAlertDismissed, setConflictAlertDismissed] = useState(false);
  const [pendingRequestsDrawerOpen, setPendingRequestsDrawerOpen] = useState(false);

  const selectedStatus = status as AppointmentStatus | 'ALL';
  const selectedResourceId = resource as CalendarResourceFilter;
  const showExternalCalendars = external;
  const externalCalendarsActive = showExternalCalendars;

  const { data: resources, isLoading: isResourcesLoading } = useListResourcesQuery(undefined, true);

  const isSpecificResourceSelected =
    selectedResourceId !== 'ALL' && selectedResourceId != null && selectedResourceId !== '';

  const resourceOptions = useMemo(() => {
    if (!resources) return [];
    return resources
      .map((resourceOption) => ({
        id: resourceOption.id,
        name: getResourceDisplayName(resourceOption, 'Unnamed Resource'),
        type: resourceOption.type
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [resources]);

  useEffect(() => {
    if (isResourcesLoading || resourceOptions.length === 0) return;
    if (selectedResourceId === 'ALL') return;
    const isValid = resourceOptions.some((r) => r.id === selectedResourceId);
    if (!isValid) {
      navigate({
        to: '/manage/calendar',
        replace: true,
        search: buildCalendarSearch({
          status,
          resource: 'ALL',
          external
        })
      } as any);
    }
  }, [resourceOptions, selectedResourceId, navigate, status, external, isResourcesLoading]);

  useEffect(() => {
    if (selectedResourceId === 'ALL') {
      setConflictAlertDismissed(false);
    }
  }, [selectedResourceId]);

  const importFeedResourceIds = useMemo(() => {
    if (!externalCalendarsActive) return [];
    if (isSpecificResourceSelected) return [selectedResourceId];
    return resourceOptions.map((r) => r.id);
  }, [externalCalendarsActive, isSpecificResourceSelected, selectedResourceId, resourceOptions]);

  const {
    feedsByResourceId,
    isFetching: isImportFeedsFetching,
    isLoading: isImportFeedsLoading
  } = useCalendarImportFeedsByResources(importFeedResourceIds, externalCalendarsActive);

  const visibilityByFeedId = useMemo((): Record<string, CalendarVisibilityMode> => {
    if (!externalCalendarsActive) return {};
    const map: Record<string, CalendarVisibilityMode> = {};
    Object.values(feedsByResourceId).forEach((feeds) => {
      feeds?.forEach((feed) => {
        map[feed.id] = getFeedVisibilityMode(feed);
      });
    });
    return map;
  }, [externalCalendarsActive, feedsByResourceId]);

  const resourceNameById = useMemo(() => {
    const map: Record<string, string> = {};
    resourceOptions.forEach((r) => {
      map[r.id] = r.name;
    });
    return map;
  }, [resourceOptions]);

  const hasResources = resourceOptions.length > 0;
  const resourcesReady = !isResourcesLoading && hasResources;
  const listQueriesEnabled = !!visibleRange && resourcesReady;
  const statsEnabled = resourcesReady;

  const statsListParams = useMemo(() => {
    if (!isSpecificResourceSelected) {
      return undefined;
    }
    return { resourceId: selectedResourceId };
  }, [isSpecificResourceSelected, selectedResourceId]);

  const { stats, isFetching: isStatsFetching } = useAppointmentStats(
    statsListParams,
    statsEnabled,
    autoRefresh ? AUTOREFRESH_INTERVAL : false
  );

  const { data: pendingRescheduleData, isLoading: isPendingRequestsLoading } =
    useListClientRescheduleRequestsQuery(
      { status: 'PENDING' },
      true,
      autoRefresh ? AUTOREFRESH_INTERVAL : false
    );
  const pendingChangeRequestCount = pendingRescheduleData?.requests?.length ?? 0;
  const pendingChangeRequests = pendingRescheduleData?.requests ?? [];

  const {
    isLoading: isListLoading,
    isFetching: isListFetching,
    isExternalDataPending,
    dataUpdatedAt,
    filteredAppointments,
    filteredExternalAppointments,
    allAppointments,
    uniqueStatuses
  } = useManageCalendarAppointments({
    visibleRange,
    selectedResourceId,
    externalCalendarsActive,
    selectedStatus,
    queriesEnabled: listQueriesEnabled,
    autoRefresh
  });

  const isCalendarLoading =
    isListLoading || (externalCalendarsActive && isImportFeedsLoading && importFeedResourceIds.length > 0);
  const isFetching =
    isListFetching ||
    isStatsFetching ||
    (externalCalendarsActive && isImportFeedsFetching);
  const showExternalLoadingBanner =
    externalCalendarsActive && isExternalDataPending && !isCalendarLoading;

  const lastUpdatedText = useLastUpdatedText(dataUpdatedAt);

  const conflictAlertItems = useMemo(() => {
    if (!externalCalendarsActive) return [];
    return collectExternalConflictItems(allAppointments, resourceNameById, t);
  }, [allAppointments, resourceNameById, externalCalendarsActive, t]);

  const conflictDays = useMemo(() => {
    if (!externalCalendarsActive) return new Set<string>();
    return new Set(conflictAlertItems.map((item) => item.overlapDayKey));
  }, [conflictAlertItems, externalCalendarsActive]);

  const appointmentsById = useMemo(() => {
    const map = new Map<string, Appointment>();
    for (const apt of allAppointments) {
      map.set(apt.id, apt);
    }
    return map;
  }, [allAppointments]);

  const conflictFingerprint = useMemo(
    () =>
      conflictAlertItems
        .map((c) => `${c.overlapDayKey}|${c.timeRange}|${c.appointmentName}|${c.externalLabel}`)
        .join(';'),
    [conflictAlertItems]
  );

  useEffect(() => {
    setConflictAlertDismissed(false);
  }, [conflictFingerprint, conflictAlertItems.length]);

  const handleClearFilters = useCallback(() => {
    navigate({
      to: '/manage/calendar',
      replace: true,
      search: buildCalendarSearch({
        status: 'ALL',
        resource,
        external
      })
    } as any);
  }, [navigate, resource, external]);

  const handleStatusChange = useCallback(
    (newStatus: AppointmentStatus | 'ALL') => {
      navigate({
        to: '/manage/calendar',
        replace: true,
        search: buildCalendarSearch({
          status: newStatus as ManageCalendarSearch['status'],
          resource,
          external
        })
      } as any);
    },
    [navigate, resource, external]
  );

  const handleResourceIdChange = useCallback(
    (resourceId: CalendarResourceFilter) => {
      navigate({
        to: '/manage/calendar',
        replace: true,
        search: buildCalendarSearch({
          status,
          resource: resourceId,
          external
        })
      } as any);
    },
    [navigate, status, external]
  );

  const handleShowExternalCalendarsChange = useCallback(
    (value: boolean) => {
      navigate({
        to: '/manage/calendar',
        replace: true,
        search: buildCalendarSearch({
          status,
          resource,
          external: value
        })
      } as any);
    },
    [navigate, status, resource]
  );

  const handleRescheduleWithAI = useCallback((_appointment: Appointment) => {}, []);

  useEffect(() => {
    registerRescheduleWithAI(handleRescheduleWithAI);
    return () => registerRescheduleWithAI(null);
  }, [handleRescheduleWithAI, registerRescheduleWithAI]);

  const createResourceId =
    isSpecificResourceSelected ? selectedResourceId : resourceOptions[0]?.id;

  const handleCreateAppointment = useCallback(() => {
    if (!createResourceId) return;
    openCreate({
      showConfirmSwitch: true,
      initialValues: {
        resourceId: createResourceId
      }
    });
  }, [createResourceId, openCreate]);

  const handleCreateAppointmentForDate = useCallback(
    (date: Dayjs) => {
      if (!createResourceId) return;
      openCreate({
        showConfirmSwitch: true,
        initialValues: {
          preferredDate: date.startOf('day'),
          resourceId: createResourceId
        }
      });
    },
    [createResourceId, openCreate]
  );

  const handleAppointmentClick = (appointment: Appointment, event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleVisibleRangeChange = useCallback((range: CalendarVisibleRange) => {
    setVisibleRange((prev) => {
      if (
        prev &&
        prev.start.getTime() === range.start.getTime() &&
        prev.end.getTime() === range.end.getTime()
      ) {
        return prev;
      }
      return range;
    });
  }, []);

  return (
    <PageTransition animation="fadeInUp">
      <CalendarHeader
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
        onCreateAppointment={handleCreateAppointment}
        isFetching={isFetching}
        lastUpdated={lastUpdatedText}
        pendingChangeRequestCount={pendingChangeRequestCount}
        onOpenPendingRequests={() => setPendingRequestsDrawerOpen(true)}
      />

      <CalendarStats stats={stats} />

      <PendingChangeRequestsDrawer
        open={pendingRequestsDrawerOpen}
        onClose={() => setPendingRequestsDrawerOpen(false)}
        requests={pendingChangeRequests}
        appointmentsById={appointmentsById}
        isLoading={isPendingRequestsLoading}
      />

      <CalendarFilters
        selectedStatus={selectedStatus}
        selectedResourceId={selectedResourceId}
        uniqueStatuses={uniqueStatuses}
        resourceOptions={resourceOptions}
        isResourcesLoading={isResourcesLoading}
        onStatusChange={handleStatusChange}
        onResourceIdChange={handleResourceIdChange}
        onClearFilters={handleClearFilters}
        showExternalCalendars={showExternalCalendars}
        onShowExternalCalendarsChange={handleShowExternalCalendarsChange}
      />

      <CalendarExternalConflictAlert
        visible={externalCalendarsActive && !conflictAlertDismissed}
        conflicts={conflictAlertItems}
        onDismiss={() => setConflictAlertDismissed(true)}
      />

      {showExternalLoadingBanner ? (
        <Alert
          type="info"
          showIcon
          title={t('appointmentComponents.filters.loadingExternalEvents')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {resourcesReady && (
        <AppointmentsCalendar
          appointments={filteredAppointments}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          onAppointmentClick={handleAppointmentClick}
          onCreateAppointmentForDate={createResourceId ? handleCreateAppointmentForDate : undefined}
          onVisibleRangeChange={handleVisibleRangeChange}
          externalAppointments={filteredExternalAppointments}
          resourceNameById={resourceNameById}
          conflictDays={conflictDays}
          showExternalCalendars={externalCalendarsActive}
          visibilityByFeedId={visibilityByFeedId}
          isLoading={isCalendarLoading}
        />
      )}
    </PageTransition>
  );
}
